"use client";
import React, { useRef, useState } from "react";
import Image from "next/image";
import { Images } from "@assets/assets";

export default function Galarystories() {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [prevTranslateX, setPrevTranslateX] = useState(0);

  const boundsRef = useRef({ min: 0, max: 0 });

  const updateBounds = () => {
    requestAnimationFrame(() => {
      const wrapperWidth = wrapperRef.current?.offsetWidth ?? 0;
      const trackWidth = trackRef.current?.scrollWidth ?? 0;
      const min = Math.min(0, wrapperWidth - trackWidth);
      boundsRef.current = { min, max: 0 };
    });
  };

  React.useEffect(() => {
    updateBounds();
    window.addEventListener("resize", updateBounds);
    return () => window.removeEventListener("resize", updateBounds);
  }, []);

  const startDrag = (pageX: number) => {
    setIsDragging(true);
    setStartX(pageX);
    setPrevTranslateX(translateX);
    // Refresh bounds just in case
    const wrapperWidth = wrapperRef.current?.offsetWidth ?? 0;
    const trackWidth = trackRef.current?.scrollWidth ?? 0;
    boundsRef.current = { min: Math.min(0, wrapperWidth - trackWidth), max: 0 };
  };

  const moveDrag = (pageX: number) => {
    if (!isDragging) return;
    const delta = pageX - startX;
    const { min, max } = boundsRef.current;
    const next = Math.max(min, Math.min(max, prevTranslateX + delta));
    setTranslateX(next);
  };

  const endDrag = () => {
    setIsDragging(false);
    setPrevTranslateX(translateX);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) =>
    startDrag(e.pageX);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    moveDrag(e.pageX);
  };
  const handleMouseUpOrLeave = () => endDrag();

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) =>
    startDrag(e.touches[0].pageX);
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) =>
    moveDrag(e.touches[0].pageX);
  const handleTouchEnd = () => endDrag();
  return (
    <section className="lg:py-[150px] relative z-2 pb-[50px] lg:pb-[50px] pt-[50px] bg-[#D9E2ED]">
      <div className="max-w-[2500px] mx-auto w-[calc(100%_-_10%)] relative z-10">
        <div className="career-header border-b lg:gap-[50px] gap-[20px] border-white pb-[20px] justify-between flex flex-col lg:flex-row items-center w-full lg:w-auto">
          <div className="career-header-left w-full lg:w-auto leading-[110%]">
            <p className="self-stretch justify-center text-[#636363] text-base font-medium uppercase tracking-tight pb-4">
              Gallery & Stories
            </p>
            <h2 className="text-[length:var(--section-title)] font-medium clash-font">
              <span className="text-[#00040b] capitalize leading-[110%]">
                Life at{" "}
              </span>
              <span className="text-[#225bd4] capitalize leading-[110%]">
                Konstant
              </span>
            </h2>
            <h2 className="self-stretch justify-start text-[length:var(--section-title)] font-medium clash-font">
              <span className="text-[#00040b]  capitalize leading-[110%]"></span>
            </h2>
          </div>
          <div className="career-header-right flex-1 w-full lg:w-auto lg:pt-[30px] max-w-[65ch]">
            <div className="w-full text-[#00040b]/70 font-normal leading-[150%] break-words text-[length:var(--industry-para)]">
              With festivals and celebrations being an important aspect of our
              workplace culture, life at Konstant is quite engaging and vibrant.
              From team milestones to cultural festivals, we focus on
              strengthening team bonding and make our work place enjoyable for
              everyone.
            </div>
          </div>
        </div>
      </div>
      <div className="w-full lg:mt-[40px] pl-[max(5%,calc(50vw-1250px))] max-w-[100vw] overflow-hidden">
        <div
          ref={wrapperRef}
          className={`relative w-full select-none cursor-grab active:cursor-grabbing`}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUpOrLeave}
          onMouseLeave={handleMouseUpOrLeave}
          onMouseMove={handleMouseMove}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            ref={trackRef}
            className="flex gap-8 py-4 will-change-transform"
            style={{
              transform: `translateX(${translateX}px)`,
              transition: isDragging ? "none" : "transform 0.2s ease-out",
            }}
          >
            <article className="bg-transparent rounded-[18px] overflow-hidden min-w-full md:min-w-[820px] lg:min-w-[980px]">
              <div className="relative aspect-[18/9] rounded-[18px] overflow-hidden">
                <Image
                  src={Images?.career.events.event1}
                  alt={"Dandiya Night At Konstant"}
                  fill
                  sizes="(min-width: 1280px) 980px, (min-width: 768px) 820px, 720px"
                  className="object-cover"
                  draggable={false}
                />
              </div>
              <div className="flex items-center gap-4 pt-4">
                <p className="text-[#00040b] text-lg md:text-xl font-medium">
                  Dandiya Night At Konstant
                </p>
                <span className="px-[26px] py-[7px] font-[600] rounded-full border-3 border-[#FFF8B5] bg-white text-[#0A0E1A] text-[16px]">
                  2025
                </span>
              </div>
            </article>
            <article className="bg-transparent rounded-[18px] overflow-hidden min-w-full md:min-w-[820px] lg:min-w-[980px]">
              <div className="relative aspect-[18/9] rounded-[18px] overflow-hidden">
                <Image
                  src={Images?.career.events.event2}
                  alt={"Tambola Competition"}
                  fill
                  sizes="(min-width: 1280px) 980px, (min-width: 768px) 820px, 720px"
                  className="object-cover"
                  draggable={false}
                />
              </div>
              <div className="flex items-center gap-4 pt-4">
                <p className="text-[#00040b] text-lg md:text-xl font-medium">
                  Tambola Competition
                </p>
                <span className="px-[26px] py-[7px] font-[600] rounded-full border-3 border-[#FFF8B5] bg-white text-[#0A0E1A] text-[16px]">
                  2024
                </span>
              </div>
            </article>
            <article className="bg-transparent rounded-[18px] overflow-hidden min-w-full md:min-w-[820px] lg:min-w-[980px]">
              <div className="relative aspect-[18/9] rounded-[18px] overflow-hidden">
                <Image
                  src={Images?.career.events.event2}
                  alt={"Tambola Competition"}
                  fill
                  sizes="(min-width: 1280px) 980px, (min-width: 768px) 820px, 720px"
                  className="object-cover"
                  draggable={false}
                />
              </div>
              <div className="flex items-center gap-4 pt-4">
                <p className="text-[#00040b] text-lg md:text-xl font-medium">
                  Tambola Competition
                </p>
                <span className="px-[26px] py-[7px] font-[600] rounded-full border-3 border-[#FFF8B5] bg-white text-[#0A0E1A] text-[16px]">
                  2024
                </span>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
